import { 
  ReactFlow, 
  Node, 
  Edge, 
  addEdge, 
  Connection, 
  useNodesState, 
  useEdgesState, 
  Controls, 
  MiniMap, 
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  ReactFlowInstance,
  Panel
} from '@xyflow/react'
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import '@xyflow/react/dist/style.css'
import { 
  MultiTargetSelector,
  EdgeConfigDialog
} from '@/components'
import type { 
  AdvancedEdgeData, 
  EdgeConfigurationData
} from '@/components'
import { 
  DataEdgeWrapper,
  LabeledEdgeWrapper,
  ButtonEdgeWrapper
} from '@/components/edges/EdgeWrappers'
import AnnotationEdge from '@/components/reactflow/edges/AnnotationEdge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { 
  Save, 
  Eye, 
  Download,
  Plus,
  MousePointer,
  Move,
  Undo, 
  Redo
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { nodeTypes as getNodeTypes } from '@/utils/nodeTypes'
import { BlueprintWithDetails, BLUEPRINT_CATEGORIES, NODE_GROUP_TYPES } from '../../types/blueprints'
import styles from './BlueprintEditor.module.css'

// Enhanced Blueprint Editor Types
interface EditorAction {
  type: 'ADD_NODE' | 'DELETE_NODE' | 'MOVE_NODE' | 'ADD_EDGE' | 'DELETE_EDGE' | 'UPDATE_NODE_DATA' | 'UPDATE_NODE_CONFIG'
  timestamp: number
  payload: any
}

interface EditorHistory {
  past: EditorAction[]
  present: EditorAction | null
  future: EditorAction[]
}

// Enhanced Validation System
interface ValidationResult {
  isValid: boolean
  errors: ValidationIssue[]
  warnings: ValidationIssue[]
  suggestions: ValidationSuggestion[]
}

interface ValidationIssue {
  id: string
  nodeId?: string
  edgeId?: string
  type: 'error' | 'warning'
  message: string
  autoFixable: boolean
  severity: 'low' | 'medium' | 'high' | 'critical'
}

interface ValidationSuggestion {
  id: string
  type: 'optimization' | 'best-practice' | 'workflow-improvement'
  message: string
  actionLabel: string
  action: () => void
}

// Enhanced Simulation System
interface SimulationNode {
  id: string
  status: 'pending' | 'running' | 'completed' | 'error' | 'skipped'
  output?: any
  executionTime?: number
  error?: string
  resourceUsage?: ResourceUsage
}

interface ResourceUsage {
  memory: number
  cpu: number
  duration: number
  apiCalls?: number
}

interface SimulationState {
  isRunning: boolean
  isPaused: boolean
  currentStep: number
  totalSteps: number
  nodes: Record<string, SimulationNode>
  executionOrder: string[]
  startTime?: number
  endTime?: number
  resourceRequirements?: ResourceRequirements
}

interface ResourceRequirements {
  estimatedMemory: number
  estimatedDuration: number
  estimatedCost?: number
  dependencies: string[]
}

const useEditorHistory = () => {
  const [history, setHistory] = useState<EditorHistory>({
    past: [],
    present: null,
    future: []
  })

  const addAction = useCallback((action: EditorAction) => {
    setHistory(prev => ({
      past: prev.present ? [...prev.past, prev.present] : prev.past,
      present: action,
      future: [] // Clear future when new action is added
    }))
  }, [])

  const undo = useCallback(() => {
    setHistory(prev => {
      if (prev.past.length === 0) return prev
      
      const previous = prev.past[prev.past.length - 1]
      const newPast = prev.past.slice(0, prev.past.length - 1)
      
      return {
        past: newPast,
        present: previous,
        future: prev.present ? [prev.present, ...prev.future] : prev.future
      }
    })
  }, [])

  const redo = useCallback(() => {
    setHistory(prev => {
      if (prev.future.length === 0) return prev
      
      const next = prev.future[0]
      const newFuture = prev.future.slice(1)
      
      return {
        past: prev.present ? [...prev.past, prev.present] : prev.past,
        present: next,
        future: newFuture
      }
    })
  }, [])

  const canUndo = history.past.length > 0
  const canRedo = history.future.length > 0

  return { addAction, undo, redo, canUndo, canRedo, currentAction: history.present, editorHistory: history }
}

// Node types available in the blueprint editor
export const BLUEPRINT_NODE_TYPES = [
  // Data Processing
  { type: 'FileResourceNode', category: 'Data', label: 'File Resource', icon: '📁', color: '#3B82F6' },
  { type: 'NodeWebExtract', category: 'Data', label: 'Web Extract', icon: '🌐', color: '#3B82F6' },
  
  // Information Processing
  { type: 'QuickStartNode', category: 'Information', label: 'Quick Start', icon: '⚡', color: '#10B981' },
  { type: 'NodePlantUML', category: 'Information', label: 'PlantUML', icon: '📊', color: '#10B981' },
  { type: 'FormWizardNode', category: 'Information', label: 'Form Wizard', icon: '📝', color: '#10B981' },
  
  // Knowledge Processing
  { type: 'SmartNotesNode', category: 'Knowledge', label: 'Smart Notes', icon: '📚', color: '#F59E0B' },
  { type: 'NodeResearcher', category: 'Knowledge', label: 'Researcher', icon: '🔍', color: '#F59E0B' },
  { type: 'NodeFileMiner', category: 'Knowledge', label: 'File Miner', icon: '⛏️', color: '#F59E0B' },
  
  // Wisdom Processing
  { type: 'NodeAgent', category: 'Wisdom', label: 'AI Agent', icon: '🤖', color: '#8B5CF6' },
  { type: 'NodeRecommendator', category: 'Wisdom', label: 'Recommender', icon: '💡', color: '#8B5CF6' },
  { type: 'NodeIntellica', category: 'Wisdom', label: 'Intellica', icon: '🧠', color: '#8B5CF6' },
  
  // Synthesis Processing
  { type: 'NodeStrategy', category: 'Synthesis', label: 'Strategy', icon: '🎯', color: '#EF4444' },
  { type: 'NodeDocumentDesigner', category: 'Synthesis', label: 'Doc Designer', icon: '📄', color: '#EF4444' },
  
  // Deliverables
  { type: 'NodeComposite', category: 'Deliverables', label: 'Composite', icon: '🔗', color: '#6B7280' },
  { type: 'NodeJavascript', category: 'Deliverables', label: 'JavaScript', icon: '💻', color: '#6B7280' },
  { type: 'ImageGeneratorNode', category: 'Deliverables', label: 'Image Gen', icon: '🎨', color: '#6B7280' },
  
  // Special Nodes
  { type: 'NodeHTMLFields', category: 'Special', label: 'HTML Fields', icon: '🏗️', color: '#EC4899' },
  { type: 'NodeGroupBlueprint', category: 'Special', label: 'Group Blueprint', icon: '📦', color: '#EC4899' },
  { type: 'NodeWorkflow', category: 'Special', label: 'Workflow', icon: '⚙️', color: '#EC4899' },
]

// Mapping from blueprint node types to actual Spechops node types
const BLUEPRINT_TO_NODE_TYPE_MAPPING: Record<string, string> = {
  'FileResourceNode': 'noderesource',
  'NodeWebExtract': 'nodewebextract',
  'QuickStartNode': 'nodequickstart',
  'NodePlantUML': 'nodediagrams',
  'FormWizardNode': 'nodeformwizard',
  'SmartNotesNode': 'nodesmartnotes',
  'NodeResearcher': 'noderesearcher',
  'NodeFileMiner': 'nodefileminer',
  'NodeAgent': 'nodeagent',
  'NodeRecommendator': 'noderecommendator',
  'NodeIntellica': 'nodeintellica',
  'NodeStrategy': 'nodestrategy',
  'NodeDocumentDesigner': 'nodedocumentdesigner',
  'NodeComposite': 'nodecomposite',
  'NodeJavascript': 'nodejavascript',
  'ImageGeneratorNode': 'nodeimagegenerator',
  'NodeHTMLFields': 'nodehtmlfields',
  'NodeGroupBlueprint': 'nodegroupblueprint', 
  'NodeWorkflow': 'nodeworkflow',
}

interface BlueprintEditorProps {
  blueprint?: BlueprintWithDetails
  onSave?: (blueprint: Partial<BlueprintWithDetails>) => void
  onCancel?: () => void
  mode?: 'create' | 'edit' | 'view'
}

interface BlueprintNode extends Node {
  data: {
    nodeType: string
    label: string
    icon: string
    color: string
    category: string
    configuration?: any
    inputs?: any[]
    outputs?: any[]
  }
}

interface NodeGroupData {
  id: string
  name: string
  description?: string
  nodeIds: string[]
  groupType: 'sequential' | 'parallel' | 'conditional'
  dependencies?: any
  configuration: any
  executionOrder: number
  isReusable: boolean
}

const initialNodes: BlueprintNode[] = []
const initialEdges: Edge[] = []

export const BlueprintEditor: React.FC<BlueprintEditorProps> = ({
  blueprint,
  onSave,
  onCancel,
  mode = 'create'
}) => {
  const { toast } = useToast()
  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null)
  
  // Blueprint metadata
  const [blueprintName, setBlueprintName] = useState(blueprint?.name || '')
  const [blueprintDescription, setBlueprintDescription] = useState(blueprint?.description || '')
  const [blueprintCategory, setBlueprintCategory] = useState(blueprint?.category || '')
  const [blueprintTags, setBlueprintTags] = useState(blueprint?.tags?.join(', ') || '')
  const [blueprintIsPublic, setBlueprintIsPublic] = useState(blueprint?.isPublic || false)
  
  // ReactFlow state
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
    // Editor state
  const [selectedNodes, setSelectedNodes] = useState<string[]>([])
  const [selectedNodeGroups, setSelectedNodeGroups] = useState<NodeGroupData[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const [activeTab, setActiveTab] = useState('design')
  const [editorMode, setEditorMode] = useState<'select' | 'pan' | 'add-node'>('select')
    // Dialogs
  const [showNodeGroupDialog, setShowNodeGroupDialog] = useState(false)
  
  // Node group creation
  const [nodeGroupName, setNodeGroupName] = useState('')
  const [nodeGroupDescription, setNodeGroupDescription] = useState('')
  const [nodeGroupType, setNodeGroupType] = useState<'sequential' | 'parallel' | 'conditional'>('sequential')
  const [nodeGroupExecutionOrder, setNodeGroupExecutionOrder] = useState(1)
  const [nodeGroupIsReusable, setNodeGroupIsReusable] = useState(true)
  // Node configuration panel
  const [showNodePropertiesDialog, setShowNodePropertiesDialog] = useState(false)
  const [editingNode, setEditingNode] = useState<BlueprintNode | null>(null)
  const [nodeConfig, setNodeConfig] = useState<Record<string, any>>({})

  // Blueprint simulation and validation state
  const [simulationState, setSimulationState] = useState<SimulationState>({
    isRunning: false,
    isPaused: false,
    currentStep: 0,
    totalSteps: 0,
    nodes: {},
    executionOrder: []
  })
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null)
  const [showSimulationDialog, setShowSimulationDialog] = useState(false)

  // Node configuration schemas for different node types
  const nodeConfigSchemas: Record<string, { type: string, label: string, placeholder?: string, options?: string[], required?: boolean }[]> = {
    // File Resource Node
    FileResourceNode: [
      { type: 'text', label: 'File Path', placeholder: 'Enter file path or URL', required: true },
      { type: 'select', label: 'File Type', options: ['PDF', 'Markdown', 'JSON', 'CSV', 'Text', 'Image', 'Other'] },
      { type: 'text', label: 'Access Credentials', placeholder: 'Optional: credentials or API key' },
    ],
    // Web Extract Node
    NodeWebExtract: [
      { type: 'text', label: 'URL', placeholder: 'Enter website URL', required: true },
      { type: 'select', label: 'Extract Type', options: ['Full Page', 'Main Content', 'Tables', 'Links', 'Images'] },
      { type: 'number', label: 'Depth', placeholder: 'Crawling depth (1-5)' },
    ],
    // Quick Start Node
    QuickStartNode: [
      { type: 'text', label: 'Project Name', placeholder: 'Enter project name', required: true },
      { type: 'textarea', label: 'Project Requirements', placeholder: 'Describe project requirements' },
      { type: 'select', label: 'Template Type', options: ['Basic', 'Advanced', 'Complete'] },
    ],
    // Plant UML Node
    NodePlantUML: [
      { type: 'textarea', label: 'UML Diagram', placeholder: 'Enter PlantUML syntax', required: true },
      { type: 'select', label: 'Diagram Type', options: ['Class Diagram', 'Sequence Diagram', 'Use Case Diagram', 'Activity Diagram', 'Component Diagram'] },
      { type: 'text', label: 'Title', placeholder: 'Diagram title' },    ],
    // Form Wizard Node
    FormWizardNode: [
      { type: 'text', label: 'Form Title', placeholder: 'Enter form title', required: true },
      { type: 'textarea', label: 'Form Description', placeholder: 'Describe the form purpose' },
      { type: 'select', label: 'Form Layout', options: ['Single Column', 'Two Column', 'Multi-step'] },
      { type: 'checkbox', label: 'Required Validation', placeholder: 'Enable field validation' },
    ],
    // Smart Notes Node
    SmartNotesNode: [
      { type: 'text', label: 'Notes Title', placeholder: 'Enter notes title', required: true },
      { type: 'textarea', label: 'Content Template', placeholder: 'Define note structure template' },
      { type: 'select', label: 'Note Type', options: ['Research', 'Meeting', 'Analysis', 'Summary'] },
      { type: 'checkbox', label: 'Auto-tagging', placeholder: 'Enable automatic tag generation' },
    ],
    // AI Agent Node
    NodeAgent: [
      { type: 'text', label: 'Agent Name', placeholder: 'Enter agent name', required: true },
      { type: 'textarea', label: 'System Prompt', placeholder: 'Define agent behavior and instructions' },
      { type: 'select', label: 'Model', options: ['GPT-4', 'GPT-3.5-turbo', 'Claude-3', 'Gemini-Pro'] },
      { type: 'number', label: 'Temperature', placeholder: 'Creativity level (0.0-1.0)' },
      { type: 'number', label: 'Max Tokens', placeholder: 'Maximum response length' },
    ],
    // Researcher Node
    NodeResearcher: [
      { type: 'text', label: 'Research Topic', placeholder: 'Enter research topic', required: true },
      { type: 'textarea', label: 'Research Questions', placeholder: 'List specific questions to investigate' },
      { type: 'select', label: 'Source Types', options: ['Web Search', 'Academic Papers', 'News Articles', 'All Sources'] },
      { type: 'number', label: 'Max Sources', placeholder: 'Maximum number of sources (1-50)' },
    ],
    // File Miner Node
    NodeFileMiner: [
      { type: 'text', label: 'File Pattern', placeholder: 'Enter file pattern (*.pdf, *.docx)', required: true },
      { type: 'text', label: 'Search Directory', placeholder: 'Directory path to search' },
      { type: 'select', label: 'Mining Type', options: ['Extract Text', 'Extract Metadata', 'Extract Images', 'Full Analysis'] },
      { type: 'checkbox', label: 'Recursive Search', placeholder: 'Search subdirectories' },
    ],
    // Strategy Node
    NodeStrategy: [
      { type: 'text', label: 'Strategy Name', placeholder: 'Enter strategy name', required: true },
      { type: 'textarea', label: 'Objectives', placeholder: 'Define strategic objectives' },
      { type: 'select', label: 'Time Horizon', options: ['Short-term (1-6 months)', 'Medium-term (6-18 months)', 'Long-term (18+ months)'] },
      { type: 'textarea', label: 'Success Metrics', placeholder: 'Define measurable success criteria' },
    ],
    // Document Designer Node
    NodeDocumentDesigner: [
      { type: 'text', label: 'Document Type', placeholder: 'Enter document type', required: true },
      { type: 'select', label: 'Template', options: ['Report', 'Proposal', 'Manual', 'Presentation', 'Custom'] },
      { type: 'select', label: 'Format', options: ['PDF', 'Word', 'Markdown', 'HTML'] },
      { type: 'textarea', label: 'Style Guide', placeholder: 'Define formatting and style preferences' },
    ],
    // Composite Node
    NodeComposite: [
      { type: 'text', label: 'Composite Name', placeholder: 'Enter composite name', required: true },
      { type: 'textarea', label: 'Combination Logic', placeholder: 'Describe how inputs should be combined' },
      { type: 'select', label: 'Output Format', options: ['JSON', 'Text', 'Structured Data', 'Custom'] },
    ],
    // JavaScript Node
    NodeJavascript: [
      { type: 'text', label: 'Function Name', placeholder: 'Enter function name', required: true },
      { type: 'textarea', label: 'JavaScript Code', placeholder: 'Enter JavaScript code', required: true },
      { type: 'text', label: 'Input Parameters', placeholder: 'Comma-separated parameter names' },
      { type: 'select', label: 'Execution Environment', options: ['Browser', 'Node.js', 'Sandbox'] },
    ],
    // Image Generator Node
    ImageGeneratorNode: [
      { type: 'text', label: 'Image Prompt', placeholder: 'Describe the image to generate', required: true },
      { type: 'select', label: 'Style', options: ['Photorealistic', 'Artistic', 'Cartoon', 'Technical', 'Abstract'] },
      { type: 'select', label: 'Size', options: ['1024x1024', '1024x768', '768x1024', '512x512'] },
      { type: 'number', label: 'Quality', placeholder: 'Quality level (1-10)' },
    ],
    // HTML Fields Node
    NodeHTMLFields: [
      { type: 'text', label: 'Field Set Name', placeholder: 'Enter field set name', required: true },
      { type: 'textarea', label: 'HTML Template', placeholder: 'Define HTML structure and fields' },
      { type: 'select', label: 'Field Type', options: ['Input Form', 'Display Template', 'Interactive Widget'] },
      { type: 'text', label: 'CSS Classes', placeholder: 'CSS classes for styling' },
    ],
    // Group Blueprint Node
    NodeGroupBlueprint: [
      { type: 'text', label: 'Blueprint Reference', placeholder: 'Reference to existing blueprint', required: true },
      { type: 'textarea', label: 'Input Mappings', placeholder: 'Map inputs to blueprint parameters' },
      { type: 'textarea', label: 'Output Mappings', placeholder: 'Map blueprint outputs to this node' },
      { type: 'checkbox', label: 'Inherit Settings', placeholder: 'Inherit parent blueprint settings' },
    ],
    // Workflow Node
    NodeWorkflow: [
      { type: 'text', label: 'Workflow Name', placeholder: 'Enter workflow name', required: true },
      { type: 'textarea', label: 'Workflow Steps', placeholder: 'Define workflow execution steps' },
      { type: 'select', label: 'Execution Mode', options: ['Sequential', 'Parallel', 'Conditional', 'Event-driven'] },
      { type: 'number', label: 'Timeout (minutes)', placeholder: 'Maximum execution time' },
    ],
    // Recommender Node
    NodeRecommendator: [
      { type: 'text', label: 'Recommendation Context', placeholder: 'Define what to recommend', required: true },
      { type: 'textarea', label: 'Criteria', placeholder: 'List recommendation criteria' },
      { type: 'select', label: 'Algorithm', options: ['Collaborative Filtering', 'Content-based', 'Hybrid', 'ML-based'] },
      { type: 'number', label: 'Max Recommendations', placeholder: 'Maximum number of recommendations' },
    ],
    // Intellica Node
    NodeIntellica: [
      { type: 'text', label: 'Analysis Type', placeholder: 'Type of intelligent analysis', required: true },
      { type: 'textarea', label: 'Analysis Parameters', placeholder: 'Define analysis parameters and scope' },
      { type: 'select', label: 'Intelligence Level', options: ['Basic', 'Advanced', 'Expert', 'Adaptive'] },
      { type: 'checkbox', label: 'Learn from Results', placeholder: 'Enable machine learning from outputs' },
    ],
  }

  // Default schema for nodes without specific schema
  const defaultNodeConfigSchema = [
    { type: 'text', label: 'Name', placeholder: 'Enter name', required: true },
    { type: 'textarea', label: 'Description', placeholder: 'Enter description' },
    { type: 'text', label: 'Tags', placeholder: 'Comma-separated tags' },
  ]

  const { addAction, undo, redo, canUndo, canRedo, editorHistory } = useEditorHistory()

  // Blueprint validation function
  const validateBlueprint = useCallback((): ValidationResult => {
    const errors: { nodeId?: string; edgeId?: string; type: 'error' | 'warning'; message: string }[] = []
    const warnings: { nodeId?: string; edgeId?: string; message: string }[] = []

    try {
      // Check for isolated nodes (no connections)
      nodes.forEach(node => {
        const hasConnections = edges.some(edge => edge.source === node.id || edge.target === node.id)
        if (!hasConnections && nodes.length > 1) {
          const nodeLabel = (node).data?.label || node.id
          warnings.push({
            nodeId: node.id,
            message: `Node "${nodeLabel}" has no connections`
          })
        }
      })

      // Check for circular dependencies
      const visited = new Set<string>()
      const recursionStack = new Set<string>()
      
      const hasCycle = (nodeId: string): boolean => {
        if (recursionStack.has(nodeId)) return true
        if (visited.has(nodeId)) return false
        
        visited.add(nodeId)
        recursionStack.add(nodeId)
        
        const outgoingEdges = edges.filter(edge => edge.source === nodeId)
        for (const edge of outgoingEdges) {
          if (hasCycle(edge.target)) return true
        }
        
        recursionStack.delete(nodeId)
        return false
      }

      for (const node of nodes) {
        if (hasCycle(node.id)) {
          errors.push({
            nodeId: node.id,
            type: 'error',
            message: 'Circular dependency detected'
          })
          break
        }
      }

      // Check for required node configurations
      nodes.forEach(node => {
        try {
          const blueprintNode = node
          const nodeType = blueprintNode.data?.nodeType || 'unknown'
          const schema = nodeConfigSchemas[nodeType] || defaultNodeConfigSchema
          const requiredFields = schema.filter(field => field.required)
          
          requiredFields.forEach(field => {
            const value = blueprintNode.data?.configuration?.[field.label]
            if (!value || (typeof value === 'string' && value.trim() === '')) {
              errors.push({
                nodeId: node.id,
                type: 'error',
                message: `Required field "${field.label}" is missing`
              })
            }
          })
        } catch (nodeError) {
          console.warn('Error validating node configuration:', nodeError)
          warnings.push({
            nodeId: node.id,
            message: 'Unable to validate node configuration'
          })
        }
      })

      // Check for orphaned edges (edges with missing nodes)
      edges.forEach(edge => {
        const sourceExists = nodes.some(node => node.id === edge.source)
        const targetExists = nodes.some(node => node.id === edge.target)
        
        if (!sourceExists || !targetExists) {
          errors.push({
            edgeId: edge.id,
            type: 'error',
            message: 'Edge connects to non-existent node'
          })
        }
      })

      // Check blueprint metadata
      if (!blueprintName.trim()) {
        errors.push({
          type: 'error',
          message: 'Blueprint name is required'
        })
      }

      if (!blueprintCategory) {
        warnings.push({
          message: 'Blueprint category is not set'
        })
      }

    } catch (validationError) {
      console.error('Validation error:', validationError)
      errors.push({
        type: 'error',
        message: 'Blueprint validation failed due to internal error'
      })
    }

    return {
      isValid: errors.length === 0,
      errors: errors.map((err, index) => ({
        id: `error_${index}`,
        ...err,
        autoFixable: false,
        severity: 'high' as const
      })),
      warnings: warnings.map((warn, index) => ({
        id: `warning_${index}`,
        type: 'warning' as const,
        ...warn,
        autoFixable: false,
        severity: 'medium' as const
      })),
      suggestions: []
    }
  }, [nodes, edges, nodeConfigSchemas, defaultNodeConfigSchema, blueprintName, blueprintCategory])

  // Blueprint simulation function
  const simulateBlueprint = useCallback(async () => {
    const validation = validateBlueprint()
    setValidationResult(validation)

    if (!validation.isValid) {
      toast({
        title: 'Validation Failed',
        description: `Found ${validation.errors.length} error(s). Please fix them before simulation.`,
        variant: 'destructive'
      })
      return
    }

    // Initialize simulation state
    const simulationNodes: Record<string, SimulationNode> = {}
    nodes.forEach(node => {
      simulationNodes[node.id] = {
        id: node.id,
        status: 'pending'
      }
    })

    // Calculate execution order (topological sort)
    const executionOrder: string[] = []
    const visited = new Set<string>()
    const tempVisited = new Set<string>()

    const visit = (nodeId: string) => {
      if (tempVisited.has(nodeId)) return // Cycle detected, already handled in validation
      if (visited.has(nodeId)) return

      tempVisited.add(nodeId)
      
      // Visit all dependencies (incoming edges)
      const dependencies = edges.filter(edge => edge.target === nodeId)
      dependencies.forEach(edge => visit(edge.source))

      tempVisited.delete(nodeId)
      visited.add(nodeId)
      executionOrder.push(nodeId)
    }

    nodes.forEach(node => visit(node.id))

    setSimulationState({
      isRunning: true,
      isPaused: false,
      currentStep: 0,
      totalSteps: executionOrder.length,
      nodes: simulationNodes,
      executionOrder,
      startTime: Date.now()
    })

    setShowSimulationDialog(true)      // Simulate node execution
      for (let i = 0; i < executionOrder.length; i++) {
        const nodeId = executionOrder[i]
        const node = nodes.find(n => n.id === nodeId)!
        
        if (!node) {
          console.error(`Node with id ${nodeId} not found during simulation`)
          continue
        }

        // Update current step
        setSimulationState(prev => ({
          ...prev,
          currentStep: i + 1,
          nodes: {
            ...prev.nodes,
            [nodeId]: { ...prev.nodes[nodeId], status: 'running' }
          }
        }))

        // Simulate processing time (300ms - 2s based on node type)
        const processingTime = Math.random() * 1700 + 300
        await new Promise(resolve => setTimeout(resolve, processingTime))

        // Simulate success/failure (95% success rate)
        const success = Math.random() > 0.05
        const nodeLabel = node.data?.label || node.id
        
        setSimulationState(prev => ({
          ...prev,
          nodes: {
            ...prev.nodes,
            [nodeId]: {
              ...prev.nodes[nodeId],
              status: success ? 'completed' : 'error',
              executionTime: processingTime,
              output: success ? `Mock output from ${nodeLabel}` : undefined,
              error: success ? undefined : 'Simulated processing error'
            }
          }
        }))

        // If node failed, skip dependent nodes
        if (!success) {
          const dependentNodes = edges
            .filter(edge => edge.source === nodeId)
            .map(edge => edge.target)
          
          dependentNodes.forEach(depNodeId => {
            setSimulationState(prev => ({
              ...prev,
              nodes: {
                ...prev.nodes,
                [depNodeId]: { ...prev.nodes[depNodeId], status: 'skipped' }
              }
            }))
          })
        }
      }// Mark simulation as complete
    setSimulationState(prev => ({
      ...prev,
      isRunning: false,
      endTime: Date.now()
    }))

    toast({
      title: 'Simulation Complete',
      description: 'Blueprint simulation has finished successfully'
    })
  }, [nodes, edges, validateBlueprint, toast])

  // Initialize editor with blueprint data
  useEffect(() => {
    if (blueprint && reactFlowInstance) {
      try {
        const nodeDefinitions = blueprint.nodeDefinitions || []
        const workflowConfig = blueprint.workflowConfig || {}
        
        // Convert blueprint data to ReactFlow nodes
        const blueprintNodes: BlueprintNode[] = nodeDefinitions.map((nodeDef: any, index: number) => {
          const nodeTypeInfo = BLUEPRINT_NODE_TYPES.find(t => t.type === nodeDef.type) || BLUEPRINT_NODE_TYPES[0]
          
          return {
            id: nodeDef.id || `node-${index}`,
            type: BLUEPRINT_TO_NODE_TYPE_MAPPING[nodeDef.type] || 'default',
            position: nodeDef.position || { x: index * 200, y: index * 100 },
            data: {
              nodeType: nodeDef.type,
              label: nodeDef.label || nodeTypeInfo.label,
              icon: nodeTypeInfo.icon,
              color: nodeTypeInfo.color,
              category: nodeTypeInfo.category,
              configuration: nodeDef.configuration || {},
              inputs: nodeDef.inputs || [],
              outputs: nodeDef.outputs || []
            }
          }
        })
        
        // Convert workflow connections to ReactFlow edges
        const blueprintEdges: Edge[] = (workflowConfig.connections || []).map((conn: any, index: number) => ({
          id: conn.id || `edge-${index}`,
          source: conn.source,
          target: conn.target,
          sourceHandle: conn.sourceHandle,
          targetHandle: conn.targetHandle,
          type: conn.type || 'default'
        }))
        
        setNodes(blueprintNodes)
        setEdges(blueprintEdges)
        
        // Load node groups
        const nodeGroups: NodeGroupData[] = (blueprint.nodeGroups || []).map(group => ({
          id: group.id,
          name: group.name,
          description: group.description,
          nodeIds: group.nodeIds,
          groupType: group.groupType,
          dependencies: group.dependencies,
          configuration: group.configuration,
          executionOrder: group.executionOrder,
          isReusable: group.isReusable
        }))
        
        setSelectedNodeGroups(nodeGroups)
      } catch (error) {
        console.error('Error loading blueprint data:', error)
        toast({
          title: 'Error',
          description: 'Failed to load blueprint data',
          variant: 'destructive'
        })
      }
    }
  }, [blueprint, reactFlowInstance])

  // Enhanced node change tracking for drag operations
  const [dragStartPositions, setDragStartPositions] = useState<Record<string, { x: number; y: number }>>({})

  // Enhanced onNodesChange to better track drag operations for undo/redo
  const onNodesChangeWithTracking = useCallback((changes: any[]) => {
    changes.forEach(change => {
      if (change.type === 'position' && change.dragging) {
        // Store initial position when drag starts
        if (!dragStartPositions[change.id]) {
          const currentNode = nodes.find(n => n.id === change.id)
          if (currentNode) {
            setDragStartPositions(prev => ({
              ...prev,
              [change.id]: { x: currentNode.position.x, y: currentNode.position.y }
            }))
          }
        }
      } else if (change.type === 'position' && !change.dragging) {
        // Drag ended - record the move action for undo/redo
        const startPos = dragStartPositions[change.id]
        if (startPos) {
          const currentNode = nodes.find(n => n.id === change.id)
          if (currentNode && (startPos.x !== currentNode.position.x || startPos.y !== currentNode.position.y)) {
            addAction({
              type: 'MOVE_NODE',
              timestamp: Date.now(),
              payload: {
                nodeId: change.id,
                oldPosition: startPos,
                newPosition: { x: change.position?.x || currentNode.position.x, y: change.position?.y || currentNode.position.y }
              }
            })
          }
          // Clear the stored position
          setDragStartPositions(prev => {
            const newPos = { ...prev }
            delete newPos[change.id]
            return newPos
          })
        }
      }
    })

    // Apply the changes normally
    onNodesChange(changes)
    setIsDirty(true)
  }, [nodes, onNodesChange, dragStartPositions, addAction])  // Handle connection creation with enhanced edge support
  const onConnect = useCallback(
    (params: Connection) => {
      // Determine edge type based on connection context
      const sourceNode = nodes.find(n => n.id === params.source)
      const targetNode = nodes.find(n => n.id === params.target)
      
      let edgeType = 'default'
      const edgeData: AdvancedEdgeData = {
        state: {
          active: false,
          executionCount: 0,
        }
      }
      
      // Set edge type based on node types or handle types
      if (sourceNode?.type?.includes('Agent') || targetNode?.type?.includes('Agent')) {
        edgeType = 'dataEdge'
        edgeData.labels = [{ text: 'Agent Data', position: 'middle' as const }]
      } else if (sourceNode?.type?.includes('Workflow') || targetNode?.type?.includes('Workflow')) {
        edgeType = 'labeledEdge'
        edgeData.labels = [{ text: 'Workflow Step', position: 'middle' as const }]
      } else if (params.sourceHandle?.includes('button') || params.targetHandle?.includes('button')) {
        edgeType = 'buttonEdge'
      }
      
      const newEdgeConnection = {
        ...params,
        type: edgeType,
        data: edgeData
      }
      
      const newEdges = addEdge(newEdgeConnection, edges)
      setEdges(newEdges)
      
      // Add to history
      addAction({ type: 'ADD_EDGE', timestamp: Date.now(), payload: newEdgeConnection })
      setIsDirty(true)
    },
    [setEdges, edges, addAction, nodes]
  )

  // Enhanced onEdgesChange to track edge deletions
  const onEdgesChangeWithTracking = useCallback((changes: any[]) => {
    changes.forEach(change => {
      if (change.type === 'remove') {
        const deletedEdge = edges.find(edge => edge.id === change.id)
        if (deletedEdge) {
          addAction({ 
            type: 'DELETE_EDGE', 
            timestamp: Date.now(), 
            payload: { edge: deletedEdge, edgeId: change.id } 
          })
        }
      }
    })
    
    onEdgesChange(changes)
    setIsDirty(true)
  }, [edges, onEdgesChange, addAction])

  // Handle node addition
  const onAddNode = useCallback((nodeType: string) => {
    if (!reactFlowInstance) return
    
    const nodeTypeInfo = BLUEPRINT_NODE_TYPES.find(t => t.type === nodeType)
    if (!nodeTypeInfo) return

    const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect()
    if (!reactFlowBounds) return

    // Calculate position for new node
    const position = reactFlowInstance.screenToFlowPosition({
      x: reactFlowBounds.width / 2,
      y: reactFlowBounds.height / 2,
    })

    const newNode: BlueprintNode = {
      id: `node-${Date.now()}`,
      type: BLUEPRINT_TO_NODE_TYPE_MAPPING[nodeType] || 'default',
      position,
      data: {
        nodeType: nodeType,
        label: nodeTypeInfo.label,
        icon: nodeTypeInfo.icon,
        color: nodeTypeInfo.color,
        category: nodeTypeInfo.category,
        configuration: {},
        inputs: [],
        outputs: []
      }
    }

    setNodes((nds) => [...nds, newNode])
    setIsDirty(true)
    addAction({ type: 'ADD_NODE', timestamp: Date.now(), payload: newNode })
  }, [reactFlowInstance, addAction])  // Handle node selection and properties
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNodes([node.id])
    
    // Double-click behavior for opening properties panel
    const nodeClickTime = Date.now()
    const lastClickTime = (node as any)._lastClickTime || 0
    ;(node as any)._lastClickTime = nodeClickTime
    
    if (nodeClickTime - lastClickTime < 300) { // Double-click threshold (300ms)
      // Open property panel
      const blueprintNode = node as BlueprintNode
      setEditingNode(blueprintNode)
      setNodeConfig(blueprintNode.data.configuration || {})
      setShowNodePropertiesDialog(true)
    }
  }, [])
  
  // Handle node property updates
  const handleUpdateNodeProperties = useCallback(() => {
    if (!editingNode) return
    
    // Create a new node object with updated configuration
    const updatedNode = {
      ...editingNode,
      data: {
        ...editingNode.data,
        configuration: nodeConfig
      }
    }
    
    // Update the node in the flow
    setNodes((nds) => 
      nds.map((n) => (n.id === updatedNode.id ? updatedNode : n))
    )
    
    // Track the change for undo/redo
    addAction({
      type: 'UPDATE_NODE_DATA',
      timestamp: Date.now(),
      payload: {
        nodeId: editingNode.id,
        oldConfig: editingNode.data.configuration,
        newConfig: nodeConfig
      }
    })
    
    setShowNodePropertiesDialog(false)
    setEditingNode(null)
    setIsDirty(true)
  }, [editingNode, nodeConfig, addAction])

  // Handle node group creation
  const handleCreateNodeGroup = () => {
    if (selectedNodes.length === 0) {
      toast({
        title: 'No Nodes Selected',
        description: 'Please select at least one node to create a group',
        variant: 'destructive'
      })
      return
    }
    
    setShowNodeGroupDialog(true)
  }

  const handleSaveNodeGroup = () => {
    if (!nodeGroupName.trim()) {
      toast({
        title: 'Name Required',
        description: 'Please enter a name for the node group',
        variant: 'destructive'
      })
      return
    }

    const newNodeGroup: NodeGroupData = {
      id: `group-${Date.now()}`,
      name: nodeGroupName,
      description: nodeGroupDescription,
      nodeIds: selectedNodes,
      groupType: nodeGroupType,
      dependencies: {},
      configuration: {},
      executionOrder: nodeGroupExecutionOrder,
      isReusable: nodeGroupIsReusable
    }

    setSelectedNodeGroups(prev => [...prev, newNodeGroup])
    setShowNodeGroupDialog(false)
    setIsDirty(true)
    addAction({ type: 'ADD_NODE', timestamp: Date.now(), payload: newNodeGroup })
    
    // Reset form
    setNodeGroupName('')
    setNodeGroupDescription('')
    setNodeGroupType('sequential')
    setNodeGroupExecutionOrder(1)
    setNodeGroupIsReusable(true)

    toast({
      title: 'Node Group Created',
      description: `Node group "${newNodeGroup.name}" has been created`
    })
  }

  // Handle blueprint save
  const handleSave = () => {
    if (!blueprintName.trim()) {
      toast({
        title: 'Name Required',
        description: 'Please enter a name for the blueprint',
        variant: 'destructive'
      })
      return
    }

    // Convert ReactFlow data to blueprint format
    const nodeDefinitions = nodes.map(node => ({
      id: node.id,
      type: (node).data.nodeType,
      label: (node).data.label,
      position: node.position,
      configuration: (node).data.configuration,
      inputs: (node).data.inputs,
      outputs: (node).data.outputs
    }))

    const workflowConfig = {
      connections: edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        type: edge.type
      })),
      nodeGroups: selectedNodeGroups
    }

    const blueprintData = {
      name: blueprintName,
      description: blueprintDescription,
      category: blueprintCategory,
      tags: blueprintTags.split(',').map(tag => tag.trim()).filter(Boolean),
      isPublic: blueprintIsPublic,
      nodeDefinitions,
      workflowConfig,
      version: (blueprint?.version || 0) + 1
    }

    onSave?.(blueprintData)
    setIsDirty(false)
  }

  // Handle preview
  const handlePreview = () => {
    toast({
      title: 'Preview Mode',
      description: 'Blueprint preview functionality will be available soon'
    })
  }

  // Handle export
  const handleExport = () => {
    const exportData = {
      metadata: {
        name: blueprintName,
        description: blueprintDescription,
        category: blueprintCategory,
        tags: blueprintTags.split(',').map(tag => tag.trim()).filter(Boolean),
        version: (blueprint?.version || 0) + 1,
        exportedAt: new Date().toISOString()
      },
      nodeDefinitions: nodes.map(node => ({
        id: node.id,
        type: (node).data.nodeType,
        label: (node).data.label,
        position: node.position,
        configuration: (node).data.configuration,
        inputs: (node).data.inputs,
        outputs: (node).data.outputs
      })),
      workflowConfig: {
        connections: edges.map(edge => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
          type: edge.type
        })),
        nodeGroups: selectedNodeGroups
      }
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${blueprintName || 'blueprint'}.json`
    a.click()
    URL.revokeObjectURL(url)

    toast({
      title: 'Blueprint Exported',
      description: 'Blueprint has been exported successfully'
    })
  }

  const nodeTypes = useMemo(() => getNodeTypes(), [])
  const edgeTypes = useMemo(() => ({ 
    annotationedge: AnnotationEdge,
    dataEdge: DataEdgeWrapper,
    labeledEdge: LabeledEdgeWrapper,
    buttonEdge: ButtonEdgeWrapper
  }), [])

  // Handle undo/redo keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keyboard shortcuts when the blueprint editor is open
      // Ignore if we're in an input/textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return
      }

      // Undo: Ctrl+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey && canUndo) {
        e.preventDefault()
        handleUndo()
      }

      // Redo: Ctrl+Y or Ctrl+Shift+Z
      if (
        ((e.ctrlKey || e.metaKey) && e.key === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')
      ) {
        if (canRedo) {
          e.preventDefault()
          handleRedo()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [canUndo, canRedo])

  // Handle undo action
  const handleUndo = useCallback(() => {
    if (!canUndo) return
    
    const actionToUndo = editorHistory.present
    undo()
    
    if (actionToUndo) {
      switch (actionToUndo.type) {
        case 'ADD_NODE':
          // Remove the added node
          setNodes(nodes => nodes.filter(node => node.id !== actionToUndo.payload.id))
          break
          
        case 'DELETE_NODE':
          // Restore the deleted node
          setNodes(nodes => [...nodes, actionToUndo.payload.node])
          break
          
        case 'MOVE_NODE': {
          // Move node back to previous position
          const { nodeId, oldPosition } = actionToUndo.payload
          setNodes(nodes => 
            nodes.map(node => 
              node.id === nodeId ? { ...node, position: oldPosition } : node
            )
          )
          break
        }
        
        case 'UPDATE_NODE_DATA': {
          // Restore previous node configuration
          const { nodeId, oldConfig } = actionToUndo.payload
          setNodes(nodes => 
            nodes.map(node => 
              node.id === nodeId 
                ? { 
                    ...node, 
                    data: { ...node.data, configuration: oldConfig } 
                  } 
                : node
            )
          )
          break
        }
        
        case 'ADD_EDGE':
          // Remove the added edge
          setEdges(edges => edges.filter(edge => edge.id !== actionToUndo.payload.id))
          break
          
        case 'DELETE_EDGE':
          // Restore the deleted edge
          setEdges(edges => [...edges, actionToUndo.payload.edge])
          break
      }
    }
    
    setIsDirty(true)
    toast({
      title: 'Action undone',
      description: `Undid ${actionToUndo?.type.toLowerCase().replace('_', ' ')}`,
      duration: 2000,
    })
  }, [canUndo, undo, editorHistory.present, toast])

  // Handle redo action
  const handleRedo = useCallback(() => {
    if (!canRedo) return
    
    redo()
    const actionToRedo = editorHistory.present
    
    if (actionToRedo) {
      switch (actionToRedo.type) {
        case 'ADD_NODE':
          // Add the node again
          setNodes(nodes => [...nodes, actionToRedo.payload])
          break
          
        case 'DELETE_NODE':
          // Delete the node again
          setNodes(nodes => nodes.filter(node => node.id !== actionToRedo.payload.nodeId))
          break
          
        case 'MOVE_NODE': {
          // Move node to new position again
          const { nodeId, newPosition } = actionToRedo.payload
          setNodes(nodes => 
            nodes.map(node => 
              node.id === nodeId ? { ...node, position: newPosition } : node
            )
          )
          break
        }
        
        case 'UPDATE_NODE_DATA': {
          // Apply new node configuration again
          const { nodeId, newConfig } = actionToRedo.payload
          setNodes(nodes => 
            nodes.map(node => 
              node.id === nodeId 
                ? { 
                    ...node, 
                    data: { ...node.data, configuration: newConfig } 
                  } 
                : node
            )
          )
          break
        }
        
        case 'ADD_EDGE':
          // Add the edge again
          setEdges(edges => [...edges, actionToRedo.payload.edge])
          break
          
        case 'DELETE_EDGE':
          // Delete the edge again
          setEdges(edges => edges.filter(edge => edge.id !== actionToRedo.payload.edgeId))
          break
      }
    }
    
    setIsDirty(true)
    toast({
      title: 'Action redone',
      description: `Redid ${actionToRedo?.type.toLowerCase().replace('_', ' ')}`,
      duration: 2000,
    })
  }, [canRedo, redo, editorHistory.present])

  // Enhanced Workflow Intelligence System
  // Temporarily commented out to fix infinite loop - function was unused
  /*
  const generateWorkflowSuggestions = useCallback((): ValidationSuggestion[] => {
    const suggestions: ValidationSuggestion[] = []
    
    // Analyze current workflow state
    const dataNodes = nodes.filter(n => n.data.category === 'Data')
    const informationNodes = nodes.filter(n => n.data.category === 'Information')
    const knowledgeNodes = nodes.filter(n => n.data.category === 'Knowledge')
    const wisdomNodes = nodes.filter(n => n.data.category === 'Wisdom')
    const synthesisNodes = nodes.filter(n => n.data.category === 'Synthesis')
    const deliverableNodes = nodes.filter(n => n.data.category === 'Deliverables')
    
    // Suggest hierarchical progression
    if (dataNodes.length > 0 && informationNodes.length === 0) {
      suggestions.push({
        id: 'add-information-processing',
        type: 'workflow-improvement',
        message: 'Consider adding Information processing nodes to transform your raw data into structured information',
        actionLabel: 'Add Quick Start Node',
        action: () => onAddNode('QuickStartNode')
      })
    }
    
    if (informationNodes.length > 0 && knowledgeNodes.length === 0) {
      suggestions.push({
        id: 'add-knowledge-processing',
        type: 'workflow-improvement',
        message: 'Add Knowledge processing to extract insights from your information',
        actionLabel: 'Add Smart Notes',
        action: () => onAddNode('SmartNotesNode')
      })
    }
    
    if (knowledgeNodes.length > 0 && wisdomNodes.length === 0) {
      suggestions.push({
        id: 'add-wisdom-processing',
        type: 'workflow-improvement',
        message: 'Consider adding AI agents to generate wisdom from your knowledge base',
        actionLabel: 'Add AI Agent',
        action: () => onAddNode('NodeAgent')
      })
    }
    
    if (wisdomNodes.length > 0 && synthesisNodes.length === 0) {
      suggestions.push({
        id: 'add-synthesis',
        type: 'workflow-improvement',
        message: 'Add synthesis nodes to create strategic outputs from your wisdom',
        actionLabel: 'Add Strategy Node',
        action: () => onAddNode('NodeStrategy')
      })
    }
    
    if (synthesisNodes.length > 0 && deliverableNodes.length === 0) {
      suggestions.push({
        id: 'add-deliverables',
        type: 'workflow-improvement',
        message: 'Create final deliverables from your strategic synthesis',
        actionLabel: 'Add Document Designer',
        action: () => onAddNode('NodeDocumentDesigner')
      })
    }
    
    // Suggest grouping for related nodes
    if (nodes.length >= 3) {
      const ungroupedNodes = nodes.filter(n => !n.parentId)
      if (ungroupedNodes.length >= 3) {
        suggestions.push({
          id: 'group-nodes',
          type: 'optimization',
          message: 'Consider grouping related nodes into blueprint containers for better organization',
          actionLabel: 'Create Group',
          action: () => setSelectedNodes(ungroupedNodes.slice(0, 3).map(n => n.id))
        })
      }
    }
    
    // Suggest connections for isolated nodes
    const isolatedNodes = nodes.filter(node => {
      const hasConnections = edges.some(edge => edge.source === node.id || edge.target === node.id)
      return !hasConnections && nodes.length > 1
    })
    
    if (isolatedNodes.length > 0) {
      suggestions.push({
        id: 'connect-isolated-nodes',
        type: 'best-practice',
        message: `${isolatedNodes.length} node(s) are not connected. Consider connecting them to create data flow`,
        actionLabel: 'Show Isolated Nodes',
        action: () => setSelectedNodes(isolatedNodes.map(n => n.id))
      })
    }
    
    return suggestions
  }, [nodes, edges, onAddNode, setSelectedNodes])
  */

  // Advanced edge system state
  const [showMultiTargetSelector, setShowMultiTargetSelector] = useState(false)
  const [showEdgeConfigDialog, setShowEdgeConfigDialog] = useState(false)
  const [currentSourceNodeId, setCurrentSourceNodeId] = useState<string>('')
  const [selectedTargets, setSelectedTargets] = useState<string[]>([])
  const [currentEdgeConfig, setCurrentEdgeConfig] = useState<EdgeConfigurationData | null>(null)

  // Stable default edge configuration to prevent infinite re-renders
  const defaultEdgeConfig = useMemo<EdgeConfigurationData>(() => ({
    id: '',
    labels: [],
  }), [])

  // Enhanced connection handling with multi-target support
  const handleMultiTargetConnection = useCallback((sourceNodeId: string) => {
    setCurrentSourceNodeId(sourceNodeId)
    setShowMultiTargetSelector(true)
  }, [])

  const handleMultiTargetConfirm = useCallback(() => {
    selectedTargets.forEach(targetNodeId => {
      const edgeId = `${currentSourceNodeId}-${targetNodeId}-${Date.now()}`
      const newEdge: Edge = {
        id: edgeId,
        source: currentSourceNodeId,
        target: targetNodeId,
        type: 'dataEdge',
        data: {
          multiTarget: true,
          targets: selectedTargets,
          state: {
            active: false,
            executionCount: 0,
          }
        }
      }
      
      setEdges(prev => addEdge(newEdge, prev))
      addAction({ type: 'ADD_EDGE', timestamp: Date.now(), payload: newEdge })
    })
    
    setShowMultiTargetSelector(false)
    setSelectedTargets([])
    setCurrentSourceNodeId('')
    setIsDirty(true)
  }, [currentSourceNodeId, selectedTargets, setEdges, addAction])

  // Edge configuration handling
  const handleEdgeDoubleClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.stopPropagation()
    
    const edgeData = edge.data || {}
    const edgeConfig: EdgeConfigurationData = {
      id: edge.id,
      labels: Array.isArray(edgeData.labels) ? edgeData.labels : [],
      processor: typeof edgeData.processor === 'string' ? edgeData.processor : undefined,
      condition: typeof edgeData.condition === 'string' ? edgeData.condition : undefined,
      multiTarget: Boolean(edgeData.multiTarget),
      targets: Array.isArray(edgeData.targets) ? edgeData.targets : [],
      dataFlow: edgeData.dataFlow && typeof edgeData.dataFlow === 'object' 
        ? { 
            dataType: (edgeData.dataFlow as any).dataType || 'unknown',
            schema: (edgeData.dataFlow as any).schema,
            sampleData: (edgeData.dataFlow as any).sampleData
          }
        : undefined
    }
    
    setCurrentEdgeConfig(edgeConfig)
    setShowEdgeConfigDialog(true)
  }, [])

  const handleEdgeConfigSave = useCallback((config: EdgeConfigurationData) => {
    setEdges(prev => prev.map(edge => 
      edge.id === config.id 
        ? { ...edge, data: { ...edge.data, ...config } }
        : edge
    ))
    setCurrentEdgeConfig(null)
    setIsDirty(true)
  }, [setEdges])

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-background">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">
            {mode === 'create' ? 'Create Blueprint' : mode === 'edit' ? 'Edit Blueprint' : 'View Blueprint'}
          </h2>
          {isDirty && <Badge variant="secondary">Unsaved changes</Badge>}
        </div>          <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleUndo} disabled={!canUndo || mode === 'view'}>
            <Undo className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleRedo} disabled={!canRedo || mode === 'view'}>
            <Redo className="h-4 w-4" />
          </Button>
          <div className="h-4 border-l border-gray-300 mx-1" />
          <Button variant="outline" size="sm" onClick={() => {
            const validation = validateBlueprint()
            setValidationResult(validation)
            toast({
              title: validation.isValid ? 'Validation Passed' : 'Validation Failed',
              description: validation.isValid 
                ? `Blueprint is valid${validation.warnings.length > 0 ? ` with ${validation.warnings.length} warning(s)` : ''}`
                : `Found ${validation.errors.length} error(s)`,
              variant: validation.isValid ? 'default' : 'destructive'
            })
          }}>
            ✓ Validate
          </Button>
          <Button variant="outline" size="sm" onClick={simulateBlueprint} disabled={nodes.length === 0}>
            ▶ Simulate
          </Button>
          <Button variant="outline" size="sm" onClick={handlePreview}>
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={mode === 'view'}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Sidebar - Toolbox */}
        <div className="w-80 border-r bg-background overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col gap-0">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="design">Design</TabsTrigger>
              <TabsTrigger value="groups">Groups</TabsTrigger>
              <TabsTrigger value="config">Config</TabsTrigger>
              <TabsTrigger value="validation">Insights</TabsTrigger>
            </TabsList>
            
            <TabsContent value="design" className="flex-1 overflow-auto">
              <div className="p-4 space-y-4">
                {/* Editor Tools */}              <Card>
                  <CardHeader className="pb-3" hasCardAction={false}>
                    <CardTitle className="text-sm">Editor Tools</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={editorMode === 'select' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEditorMode('select')}
                      >
                        <MousePointer className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={editorMode === 'pan' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEditorMode('pan')}
                      >
                        <Move className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <Separator />
                    
                    <Button
                      size="sm"
                      onClick={handleCreateNodeGroup}
                      disabled={selectedNodes.length === 0}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Group
                    </Button>
                  </CardContent>
                </Card>

                {/* Node Types */}              <Card>
                  <CardHeader className="pb-3" hasCardAction={false}>
                    <CardTitle className="text-sm">Node Types</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {Object.entries(
                      BLUEPRINT_NODE_TYPES.reduce((acc, nodeType) => {
                        if (!acc[nodeType.category]) acc[nodeType.category] = []
                        acc[nodeType.category].push(nodeType)
                        return acc
                      }, {} as Record<string, typeof BLUEPRINT_NODE_TYPES>)
                    ).map(([category, nodeTypes]) => (
                      <div key={category} className="space-y-1">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {category}
                        </div>
                        {nodeTypes.map((nodeType) => (
                          <Button
                            key={nodeType.type}
                            variant="ghost"
                            size="sm"
                            onClick={() => onAddNode(nodeType.type)}
                            className="w-full justify-start text-left h-auto p-2"
                          >
                            <span className="mr-2">{nodeType.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium truncate">{nodeType.label}</div>
                            </div>
                          </Button>
                        ))}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="groups" className="flex-1 overflow-auto">
              <div className="p-4 space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">Node Groups</h3>
                    <Button size="sm" onClick={handleCreateNodeGroup} disabled={selectedNodes.length === 0}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {selectedNodeGroups.length === 0 ? (
                    <div className="text-center py-8 text-sm text-muted-foreground">
                      No node groups created yet
                    </div>
                  ) : (
                    selectedNodeGroups.map((group) => (
                      <Card key={group.id} className="p-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-sm">{group.name}</h4>
                            <Badge variant="secondary" className="text-xs">
                              {group.groupType}
                            </Badge>
                          </div>
                          {group.description && (
                            <p className="text-xs text-muted-foreground">{group.description}</p>
                          )}
                          <div className="text-xs text-muted-foreground">
                            {group.nodeIds.length} node{group.nodeIds.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="config" className="flex-1 overflow-auto">
              <div className="p-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="blueprint-name">Blueprint Name</Label>
                  <Input
                    id="blueprint-name"
                    value={blueprintName}
                    onChange={(e) => setBlueprintName(e.target.value)}
                    placeholder="Enter blueprint name"
                    disabled={mode === 'view'}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="blueprint-description">Description</Label>
                  <Textarea
                    id="blueprint-description"
                    value={blueprintDescription}
                    onChange={(e) => setBlueprintDescription(e.target.value)}
                    placeholder="Enter blueprint description"
                    disabled={mode === 'view'}
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="blueprint-category">Category</Label>
                  <Select value={blueprintCategory} onValueChange={setBlueprintCategory} disabled={mode === 'view'}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {BLUEPRINT_CATEGORIES.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="blueprint-tags">Tags (comma-separated)</Label>
                  <Input
                    id="blueprint-tags"
                    value={blueprintTags}
                    onChange={(e) => setBlueprintTags(e.target.value)}
                    placeholder="tag1, tag2, tag3"
                    disabled={mode === 'view'}
                  />
                </div>
                  <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="blueprint-public"
                    checked={blueprintIsPublic}
                    onCheckedChange={(checked) => setBlueprintIsPublic(checked as boolean)}
                    disabled={mode === 'view'}
                  />
                  <Label htmlFor="blueprint-public" className="text-sm">
                    Make public
                  </Label>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="validation" className="flex-1 overflow-auto">
              <div className="p-4 space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-4">Workflow Insights</h3>
                  
                  {/* Validation Errors */}
                  {validationResult && validationResult.errors.length > 0 && (
                    <div className="space-y-2 mb-6">
                      <h4 className="font-medium text-red-700">Issues to Fix</h4>
                      {validationResult.errors.map((error) => (
                        <div key={error.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm text-red-800">{error.message}</p>
                              {error.severity && (
                                <Badge variant="destructive" className="mt-1 text-xs">
                                  {error.severity}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Validation Warnings */}
                  {validationResult && validationResult.warnings.length > 0 && (
                    <div className="space-y-2 mb-6">
                      <h4 className="font-medium text-yellow-700">Warnings</h4>
                      {validationResult.warnings.map((warning) => (
                        <div key={warning.id} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm text-yellow-800">{warning.message}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Smart Suggestions */}
                  {validationResult && validationResult.suggestions.length > 0 && (
                    <div className="space-y-2 mb-6">
                      <h4 className="font-medium text-blue-700">Smart Suggestions</h4>
                      {validationResult.suggestions.map((suggestion) => (
                        <div key={suggestion.id} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-blue-800">{suggestion.message}</p>
                              <Badge variant="outline" className="mt-1 text-xs">
                                {suggestion.type}
                              </Badge>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={suggestion.action}
                              className="ml-2"
                            >
                              {suggestion.actionLabel}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Workflow Analysis */}
                  <div className="space-y-4">
                    <h4 className="font-medium">Workflow Analysis</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-2xl font-bold">{nodes.length}</div>
                          <p className="text-sm text-muted-foreground">Total Nodes</p>
                        </CardContent>
                      </Card>
                      
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-2xl font-bold">{edges.length}</div>
                          <p className="text-sm text-muted-foreground">Connections</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Hierarchy Distribution */}
                    <div>
                      <h5 className="font-medium text-sm mb-2">Data Hierarchy Distribution</h5>
                      <div className="space-y-2">
                        {['Data', 'Information', 'Knowledge', 'Wisdom', 'Synthesis', 'Deliverables'].map(category => {
                          const count = nodes.filter(n => n.data.category === category).length
                          return (
                            <div key={category} className="flex items-center justify-between text-sm">
                              <span>{category}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-500 h-2 rounded-full" 
                                    style={{ width: `${nodes.length > 0 ? (count / nodes.length) * 100 : 0}%` }}
                                  />
                                </div>
                                <span className="w-8 text-right">{count}</span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Main Canvas */}
        <div className="flex-1 relative" ref={reactFlowWrapper}>
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChangeWithTracking}
              onEdgesChange={onEdgesChangeWithTracking}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onEdgeDoubleClick={handleEdgeDoubleClick}
              onInit={setReactFlowInstance}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              attributionPosition="bottom-left"
            >
              <Controls />
              <MiniMap />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
              
              <Panel position="top-left" className="flex gap-2">
                <Button
                  variant={editorMode === 'select' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setEditorMode('select')}
                >
                  <MousePointer className="h-4 w-4" />
                </Button>
                <Button
                  variant={editorMode === 'pan' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setEditorMode('pan')}
                >
                  <Move className="h-4 w-4" />
                </Button>
                {selectedNodes.length === 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleMultiTargetConnection(selectedNodes[0])}
                    title="Create multi-target connection from selected node"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Multi-Connect
                  </Button>
                )}
              </Panel>
              
              {selectedNodes.length > 0 && (
                <Panel position="top-right">
                  <div className="bg-background border rounded-md p-2 shadow-md">
                    <div className="text-sm text-muted-foreground">
                      {selectedNodes.length} node{selectedNodes.length !== 1 ? 's' : ''} selected
                    </div>
                  </div>
                </Panel>
              )}
            </ReactFlow>
          </ReactFlowProvider>
        </div>
      </div>

      {/* Node Group Creation Dialog */}
      <Dialog open={showNodeGroupDialog} onOpenChange={setShowNodeGroupDialog}>
        <DialogContent className="sm:max-w-[425px]">          <DialogHeader>
            <DialogTitle>Create Node Group</DialogTitle>
            <DialogDescription>
              Create a new group to organize related nodes in your blueprint.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="group-name">Group Name</Label>
              <Input
                id="group-name"
                value={nodeGroupName}
                onChange={(e) => setNodeGroupName(e.target.value)}
                placeholder="Enter group name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="group-description">Description</Label>
              <Textarea
                id="group-description"
                value={nodeGroupDescription}
                onChange={(e) => setNodeGroupDescription(e.target.value)}
                placeholder="Enter group description"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="group-type">Execution Type</Label>
              <Select value={nodeGroupType} onValueChange={(value: any) => setNodeGroupType(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select execution type" />
                </SelectTrigger>
                <SelectContent>
                  {NODE_GROUP_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="execution-order">Execution Order</Label>
              <Input
                id="execution-order"
                type="number"
                value={nodeGroupExecutionOrder}
                onChange={(e) => setNodeGroupExecutionOrder(parseInt(e.target.value) || 1)}
                min="1"
              />
            </div>
              <div className="flex items-center space-x-2">
              <Checkbox 
                id="group-reusable"
                checked={nodeGroupIsReusable}
                onCheckedChange={(checked) => setNodeGroupIsReusable(checked as boolean)}
              />
              <Label htmlFor="group-reusable" className="text-sm">
                Make reusable
              </Label>
            </div>
            
            <div className="text-sm text-muted-foreground">
              Selected nodes: {selectedNodes.length}
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNodeGroupDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveNodeGroup}>
                Create Group
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Node Properties Dialog */}
      <Dialog open={showNodePropertiesDialog} onOpenChange={(open) => {
        if (!open) setShowNodePropertiesDialog(false)
      }}>
        <DialogContent className="sm:max-w-[500px]">          <DialogHeader>
            <DialogTitle>
              {editingNode?.data.icon} {editingNode?.data.label} Properties
            </DialogTitle>
            <DialogDescription>
              Configure the properties and settings for this node.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {editingNode && (
              <>
                {/* Dynamic Fields based on Node Type */}
                {(nodeConfigSchemas[editingNode.data.nodeType] || defaultNodeConfigSchema).map((field, index) => (
                  <div key={index} className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor={`field-${index}`} className="text-right">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    
                    {field.type === 'text' && (
                      <Input
                        id={`field-${index}`}
                        className="col-span-3"
                        placeholder={field.placeholder}
                        value={nodeConfig[field.label] || ''}
                        onChange={(e) => setNodeConfig(prev => ({ ...prev, [field.label]: e.target.value }))}
                      />
                    )}
                    
                    {field.type === 'textarea' && (
                      <Textarea
                        id={`field-${index}`}
                        className="col-span-3"
                        placeholder={field.placeholder}
                        value={nodeConfig[field.label] || ''}
                        onChange={(e) => setNodeConfig(prev => ({ ...prev, [field.label]: e.target.value }))}
                      />
                    )}
                    
                    {field.type === 'select' && field.options && (
                      <Select
                        value={nodeConfig[field.label] || ''}
                        onValueChange={(value) => setNodeConfig(prev => ({ ...prev, [field.label]: value }))}
                      >
                        <SelectTrigger className="col-span-3">
                          <SelectValue placeholder={`Select ${field.label}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    

                    
                    {field.type === 'number' && (
                      <Input
                        id={`field-${index}`}
                        className="col-span-3"
                        type="number"
                        placeholder={field.placeholder}
                        value={nodeConfig[field.label] || ''}
                        onChange={(e) => setNodeConfig(prev => ({ ...prev, [field.label]: e.target.value }))}
                      />
                    )}
                    
                    {field.type === 'checkbox' && (
                      <div className="col-span-3 flex items-center">
                        <Checkbox
                          id={`field-${index}`}
                          checked={!!nodeConfig[field.label]}
                          onCheckedChange={(checked) => setNodeConfig(prev => ({ ...prev, [field.label]: !!checked }))}
                        />
                        <Label htmlFor={`field-${index}`} className="ml-2">
                          {field.placeholder || field.label}
                        </Label>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowNodePropertiesDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateNodeProperties}>
              Save Changes
            </Button>
          </div>        </DialogContent>
      </Dialog>

      {/* Blueprint Simulation Dialog */}
      <Dialog open={showSimulationDialog} onOpenChange={setShowSimulationDialog}>
        <DialogContent className="sm:max-w-[600px]">          <DialogHeader>
            <DialogTitle>Blueprint Simulation</DialogTitle>
            <DialogDescription>
              Simulate the execution of your blueprint to test its flow and logic.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Simulation Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{simulationState.currentStep} / {simulationState.totalSteps}</span>
              </div>              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={styles.progressFill}
                  style={{ '--progress-width': `${(simulationState.currentStep / simulationState.totalSteps) * 100}%` } as React.CSSProperties}
                />
              </div>
            </div>

            {/* Execution Status */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {simulationState.executionOrder.map((nodeId) => {
                const node = nodes.find(n => n.id === nodeId)!
                const simNode = simulationState.nodes[nodeId]
                if (!node || !simNode) return null

                return (
                  <div key={nodeId} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <span>{node.data.icon}</span>
                      <span className="font-medium">{node.data.label}</span>
                      <Badge 
                        variant={
                          simNode.status === 'completed' ? 'default' :
                          simNode.status === 'running' ? 'secondary' :
                          simNode.status === 'error' ? 'destructive' :
                          simNode.status === 'skipped' ? 'outline' : 'secondary'
                        }
                      >
                        {simNode.status}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {simNode.executionTime && `${simNode.executionTime.toFixed(0)}ms`}
                      {simNode.error && (
                        <div className="text-red-500 text-xs">{simNode.error}</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Simulation Summary */}
            {!simulationState.isRunning && simulationState.endTime && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Simulation Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Time:</span>
                    <span className="ml-2 font-medium">
                      {((simulationState.endTime - (simulationState.startTime || 0)) / 1000).toFixed(2)}s
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Success Rate:</span>
                    <span className="ml-2 font-medium">
                      {Math.round((Object.values(simulationState.nodes).filter(n => n.status === 'completed').length / Object.values(simulationState.nodes).length) * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Validation Results */}
            {validationResult && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Validation Results</h4>
                {validationResult.errors.length > 0 && (
                  <div className="space-y-1">
                    <h5 className="text-sm font-medium text-red-600">Errors:</h5>
                    {validationResult.errors.map((error, index) => (
                      <div key={index} className="text-sm text-red-600 bg-red-50 p-2 rounded">
                        {error.message}
                      </div>
                    ))}
                  </div>
                )}
                {validationResult.warnings.length > 0 && (
                  <div className="space-y-1 mt-2">
                    <h5 className="text-sm font-medium text-yellow-600">Warnings:</h5>
                    {validationResult.warnings.map((warning, index) => (
                      <div key={index} className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
                        {warning.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowSimulationDialog(false)}>
              Close
            </Button>
            {!simulationState.isRunning && (
              <Button onClick={simulateBlueprint}>
                Run Again
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Multi-Target Selector Dialog */}
      <MultiTargetSelector
        open={showMultiTargetSelector}
        onClose={() => {
          setShowMultiTargetSelector(false)
          setSelectedTargets([])
          setCurrentSourceNodeId('')
        }}
        sourceNodeId={currentSourceNodeId}
        nodes={nodes}
        selectedTargets={selectedTargets}
        onTargetsChange={setSelectedTargets}
        onConfirm={handleMultiTargetConfirm}
      />

      {/* Edge Configuration Dialog */}
      <EdgeConfigDialog
        open={showEdgeConfigDialog}
        onClose={() => {
          setShowEdgeConfigDialog(false)
          setCurrentEdgeConfig(null)
        }}
        edgeData={currentEdgeConfig || defaultEdgeConfig}
        onSave={handleEdgeConfigSave}
      />
    </div>
  )
}
